/**
 * Sentry init for AutoAds frontend.
 *
 * Skipped entirely when REACT_APP_SENTRY_DSN is missing (dev / local).
 * Imported once from src/index.js, BEFORE ReactDOM.createRoot, so the SDK
 * can install its global error/unhandledrejection handlers as early as
 * possible.
 *
 * Quotas we're optimizing for (Sentry free plan):
 *   - 5k errors / mo  → no sampling on errors, every error ships
 *   - tracing OFF     → would burn through 10k traces in days
 *
 * ⚠️ Session Replay DISABLED (2026-05-18):
 *   The replay integration uses `new Function()` under the hood (rrweb
 *   serializer), which is blocked by our hosting CSP (no `unsafe-eval`).
 *   Result: Sentry init throws → React never mounts → users can't log in.
 *   We removed Replay entirely; errors + stack traces still work fine.
 *   If we need Replay later, the platform CSP has to allow `unsafe-eval`
 *   in script-src OR we self-host with our own CSP — both trade-offs.
 */

import * as Sentry from "@sentry/react";

let initialized = false;

export const initSentry = () => {
  if (initialized) return;
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) {
    // Dev mode — stay silent.
    return;
  }

  const environment =
    process.env.REACT_APP_SENTRY_ENVIRONMENT ||
    (process.env.NODE_ENV === "production" ? "production" : "development");

  const release =
    process.env.REACT_APP_GIT_COMMIT_SHA ||
    process.env.REACT_APP_SENTRY_RELEASE ||
    undefined;

  try {
    _initSentry({ dsn, environment, release });
    initialized = true;
  } catch (e) {
    // Last-resort guard. If Sentry SDK itself throws on init (CSP, etc.),
    // do NOT take the whole app down with it. Log to console so we can
    // diagnose without losing the user's session.
    // eslint-disable-next-line no-console
    console.warn("[sentry] init failed, error tracking disabled:", e?.message || e);
  }
};

const _initSentry = ({ dsn, environment, release }) => {
  Sentry.init({
    dsn,
    environment,
    release,

    // ── Error capture ─────────────────────────────────────────────
    // Catch everything by default; we filter noise in beforeSend.
    sampleRate: 1.0,

    // ── Performance (off for now) ────────────────────────────────
    // Tracing eats the 10k/mo quota fast. Re-enable when we need it.
    tracesSampleRate: 0,

    // ── Session Replay DISABLED ──────────────────────────────────
    // See top-of-file comment. Sentry's rrweb-based replay calls
    // `new Function()` which our CSP blocks. Keeping these keys at
    // zero is harmless and documents the intent for future devs.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,

    integrations: [
      // Only add CSP-safe integrations here.
      // replayIntegration() = NO (uses new Function — blocked by CSP).
      // browserTracingIntegration() = NO (we have tracesSampleRate=0).
      Sentry.browserApiErrorsIntegration?.() ?? null,
    ].filter(Boolean),

    // ── Noise filtering ──────────────────────────────────────────
    ignoreErrors: [
      // Browser extensions
      "top.GLOBALS",
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      // Network blips — user's wifi flickered, not our bug
      "Network Error",
      "NetworkError when attempting to fetch resource",
      "Failed to fetch",
      "Load failed",
      "AbortError",
      // ChunkLoadError happens during deploys when old client requests new chunk
      "ChunkLoadError",
      "Loading chunk",
      "Loading CSS chunk",
    ],

    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^moz-extension:\/\//i,
      // Third-party scripts we don't control
      /googletagmanager\.com/i,
      /google-analytics\.com/i,
    ],

    beforeSend(event, hint) {
      // Drop CORS-stripped "Script error." which has zero info
      const msg = event.message || hint?.originalException?.message || "";
      if (msg === "Script error." || msg === "Script error") return null;

      // Don't ship events from localhost
      if (typeof window !== "undefined" && window.location.hostname === "localhost") {
        return null;
      }

      return event;
    },

    // We DO NOT want default PII (IP addresses, etc.)
    sendDefaultPii: false,
  });
};

/** Tag the current Sentry scope with the active user. */
export const setSentryUser = (user) => {
  if (!initialized) return;
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: user.user_id || user.id,
    email_domain: (user.email || "").split("@")[1] || undefined,
  });
  if (user.plan) Sentry.setTag("plan", user.plan);
};

export { Sentry };